import React from 'react';
import ReactSlider from 'react-slider';
import tinycolor from 'tinycolor2';
import { Box, styled } from '@staffino/primitive-controls';

const StyledSlider = styled(ReactSlider)`
  width: 100%;
  max-width: 30rem;
  margin: 0 auto;
  height: 6px;
  padding: 2rem 0;
`;

const Thumb = styled.span<{ value: number; color: string }>((props) => ({
  height: '40px',
  width: '40px',
  color: props.value > -1 ? props.color || '#00b92e' : '#d8e1eb',
  backgroundColor: props.value > -1 ? props.color || '#00b92e' : '#fff',
  border: '1px solid',
  marginTop: '-19px',
  boxShadow: '#ebebeb 0 2px 2px',
  borderRadius: '50%',
  outline: 'none',
  cursor: 'grab',
  ':hover': {
    boxShadow: `0rem 7px 15px 0rem ${tinycolor(
      props.value > -1 ? props.color || '#00b92e' : '#fff',
    )
      .setAlpha(0.3)
      .toRgbString()}`,
  },
}));

const Bar = styled(Box)<{ unset?: boolean }>(({ unset }) => ({
  height: '14px',
  width: '2px',
  backgroundColor: unset ? '#d8e1eb' : '#fff',
  marginLeft: '1px',
  marginRight: '1px',
  display: 'flex',
}));

const StyledTrack = styled.div({
  height: '6px',
  backgroundColor: '#d8e1eb',
});

const BoundLabel = styled(Box)({
  color: '#a3b2bf',
  fontWeight: 'bold',
  fontStretch: 'normal',
  fontSize: '0.875rem',
  wordBreak: 'normal' as const,
});

export const LowerBoundLabel = styled(BoundLabel)<any>({
  textAlign: 'start',
});

export const UpperBoundLabel = styled(BoundLabel)<any>({
  textAlign: 'end',
});

const HiddenInput = styled.input({
  position: 'absolute',
  width: '0px',
  height: '0px',
  opacity: '0',
});

type Props = {
  name: string;
  value?: number;
  colorStyle?: string;
  step: number;
  width?: number;
  lowerBoundScore?: number;
  upperBoundScore?: number;
  highlighted?: boolean;
  onChange?: (score: number) => void;
};

const Slider: React.ForwardRefExoticComponent<
  Props & React.RefAttributes<HTMLInputElement>
> = React.forwardRef(
  (
    {
      name,
      value,
      colorStyle,
      step,
      lowerBoundScore,
      upperBoundScore,
      onChange,
    }: Props,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    return (
      <>
        <HiddenInput
          ref={ref}
          type="text"
          name={name}
          defaultValue={value}
          value={value}
          id={`${name}.${value}`}
        />
        <StyledSlider
          min={lowerBoundScore}
          max={upperBoundScore}
          step={step}
          value={value ?? (upperBoundScore + lowerBoundScore) / 2}
          onSliderClick={onChange}
          onAfterChange={onChange}
          renderTrack={({ as: _, ...props }) => <StyledTrack {...props} />}
          renderThumb={({ as: _, ...props }) => (
            <Thumb {...props} value={value} color={colorStyle}>
              <Box
                justifyContent="center"
                flexDirection={'row' as const}
                mt={12}
              >
                <Bar unset={value === undefined} />
                <Bar unset={value === undefined} />
                <Bar unset={value === undefined} />
              </Box>
            </Thumb>
          )}
        />
      </>
    );
  },
);

export default Slider;
